/**
 * CaretPerformanceMonitor - Performance tracking for caret operations
 * 
 * Monitors and analyzes performance of caret positioning system
 * Provides real-time metrics and alerts for performance issues
 * Designed for EdgeDocs production monitoring and optimization
 */

import { PerformanceMetrics, PerformanceTarget } from '../interfaces/CaretInterfaces';

interface OperationRecord {
  operation: string;
  duration: number;
  timestamp: number;
  metadata?: any;
}

interface PerformanceAlert {
  type: 'slow_operation' | 'memory_limit' | 'cache_miss_rate' | 'error_rate';
  message: string;
  threshold: number;
  actual: number;
  timestamp: number;
}

export class CaretPerformanceMonitor {
  private operationHistory: OperationRecord[] = [];
  private alerts: PerformanceAlert[] = [];
  private maxHistorySize: number = 1000;
  
  // Performance targets
  private targets: PerformanceTarget = {
    hitTestTime: 1.0,      // 1ms target
    positioningTime: 0.5,  // 0.5ms target
    cacheHitRate: 0.90,    // 90% cache hit rate
    memoryLimit: 10 * 1024 * 1024 // 10MB memory limit
  };
  
  // Current metrics
  private currentMetrics: PerformanceMetrics = {
    hitTestTime: 0,
    positioningTime: 0,
    renderingTime: 0,
    cacheHitRate: 0,
    totalOperations: 0,
    memoryUsage: {
      spatialIndex: 0,
      positionCache: 0,
      total: 0
    }
  };
  
  // Cache statistics
  private cacheHits: number = 0;
  private cacheMisses: number = 0;
  
  // ============================================================================
  // OPERATION TIMING
  // ============================================================================
  
  /**
   * Start timing an operation
   */
  startOperation(operation: string, metadata?: any): () => void {
    const startTime = performance.now();
    
    return () => {
      const duration = performance.now() - startTime;
      this.recordOperation(operation, duration, metadata);
    };
  }
  
  /**
   * Record completed operation
   */
  recordOperation(operation: string, duration: number, metadata?: any): void {
    const record: OperationRecord = {
      operation,
      duration,
      timestamp: Date.now(),
      metadata
    };
    
    this.operationHistory.push(record);
    
    // Maintain history size limit
    if (this.operationHistory.length > this.maxHistorySize) {
      this.operationHistory.shift();
    }
    
    // Update current metrics
    this.updateMetrics();
    
    // Check for performance alerts
    this.checkPerformanceAlerts(operation, duration);
  }
  
  /**
   * Record cache hit/miss
   */
  recordCacheHit(hit: boolean): void {
    if (hit) {
      this.cacheHits++;
    } else {
      this.cacheMisses++;
    }
    
    // Update cache hit rate
    const total = this.cacheHits + this.cacheMisses;
    this.currentMetrics.cacheHitRate = total > 0 ? this.cacheHits / total : 0;
  }
  
  /**
   * Record memory usage
   */
  recordMemoryUsage(spatialIndex: number, positionCache: number): void {
    this.currentMetrics.memoryUsage = {
      spatialIndex,
      positionCache,
      total: spatialIndex + positionCache
    };
    
    // Check memory limit
    if (this.currentMetrics.memoryUsage.total > this.targets.memoryLimit) {
      this.addAlert({
        type: 'memory_limit',
        message: `Memory usage exceeded limit: ${(this.currentMetrics.memoryUsage.total / 1024 / 1024).toFixed(2)}MB`,
        threshold: this.targets.memoryLimit,
        actual: this.currentMetrics.memoryUsage.total,
        timestamp: Date.now()
      });
    }
  }
  
  // ============================================================================
  // METRICS CALCULATION
  // ============================================================================
  
  private updateMetrics(): void {
    const recent = this.operationHistory.slice(-100); // Last 100 operations
    
    this.currentMetrics.hitTestTime = this.averageTime(recent, 'hitTest');
    this.currentMetrics.positioningTime = this.averageTime(recent, 'positioning');
    this.currentMetrics.renderingTime = this.averageTime(recent, 'rendering');
    this.currentMetrics.totalOperations = this.operationHistory.length;
  }
  
  private averageTime(operations: OperationRecord[], type: string): number {
    const filtered = operations.filter(op => op.operation.includes(type));
    if (filtered.length === 0) return 0;
    
    const total = filtered.reduce((sum, op) => sum + op.duration, 0);
    return total / filtered.length;
  }
  
  private checkPerformanceAlerts(operation: string, duration: number): void {
    // Check hit test performance
    if (operation.includes('hitTest') && duration > this.targets.hitTestTime) {
      this.addAlert({
        type: 'slow_operation',
        message: `Slow hit test: ${duration.toFixed(2)}ms (target: ${this.targets.hitTestTime}ms)`,
        threshold: this.targets.hitTestTime,
        actual: duration,
        timestamp: Date.now()
      });
    }
    
    // Check positioning performance
    if (operation.includes('positioning') && duration > this.targets.positioningTime) {
      this.addAlert({
        type: 'slow_operation',
        message: `Slow positioning: ${duration.toFixed(2)}ms (target: ${this.targets.positioningTime}ms)`,
        threshold: this.targets.positioningTime,
        actual: duration,
        timestamp: Date.now()
      });
    }
    
    // Check cache hit rate
    if (this.currentMetrics.cacheHitRate < this.targets.cacheHitRate) {
      this.addAlert({
        type: 'cache_miss_rate',
        message: `Low cache hit rate: ${(this.currentMetrics.cacheHitRate * 100).toFixed(1)}% (target: ${(this.targets.cacheHitRate * 100).toFixed(1)}%)`,
        threshold: this.targets.cacheHitRate,
        actual: this.currentMetrics.cacheHitRate,
        timestamp: Date.now()
      });
    }
  }
  
  private addAlert(alert: PerformanceAlert): void {
    this.alerts.push(alert);
    
    // Keep only recent alerts (last 50)
    if (this.alerts.length > 50) {
      this.alerts.shift();
    }
    
    // Log only critical alerts (memory_limit or severe performance issues)
    if (alert.type === 'memory_limit' || alert.actual > alert.threshold * 2) {
      console.warn(`🚨 [CaretPerformanceMonitor] CRITICAL ALERT:`, alert);
    }
    // Skip logging other performance alerts to reduce console noise
  }
  
  // ============================================================================
  // PUBLIC API
  // ============================================================================
  
  /**
   * Get current performance metrics
   */
  getMetrics(): PerformanceMetrics {
    return { ...this.currentMetrics };
  }
  
  /**
   * Get performance targets
   */
  getTargets(): PerformanceTarget {
    return { ...this.targets };
  }
  
  /**
   * Update performance targets
   */
  setTargets(targets: Partial<PerformanceTarget>): void {
    this.targets = { ...this.targets, ...targets };
    console.log('[CaretPerformanceMonitor] Performance targets updated:', this.targets);
  }
  
  /**
   * Get recent performance alerts
   */
  getAlerts(maxAge: number = 60000): PerformanceAlert[] {
    const cutoff = Date.now() - maxAge;
    return this.alerts.filter(alert => alert.timestamp > cutoff);
  }
  
  /**
   * Get comprehensive performance report
   */
  getPerformanceReport(): string {
    const metrics = this.getMetrics();
    const recentAlerts = this.getAlerts();
    
    return `
EdgeDocs Caret Performance Report:
=====================================

Performance Metrics:
- Hit Testing: ${metrics.hitTestTime.toFixed(2)}ms avg (target: ${this.targets.hitTestTime}ms)
- Positioning: ${metrics.positioningTime.toFixed(2)}ms avg (target: ${this.targets.positioningTime}ms)  
- Rendering: ${metrics.renderingTime.toFixed(2)}ms avg
- Cache Hit Rate: ${(metrics.cacheHitRate * 100).toFixed(1)}% (target: ${(this.targets.cacheHitRate * 100).toFixed(1)}%)
- Total Operations: ${metrics.totalOperations}

Memory Usage:
- Spatial Index: ${(metrics.memoryUsage.spatialIndex / 1024).toFixed(2)}KB
- Position Cache: ${(metrics.memoryUsage.positionCache / 1024).toFixed(2)}KB
- Total: ${(metrics.memoryUsage.total / 1024).toFixed(2)}KB (limit: ${(this.targets.memoryLimit / 1024).toFixed(2)}KB)

Recent Alerts: ${recentAlerts.length}
${recentAlerts.map(alert => `- ${alert.type}: ${alert.message}`).join('\n')}

Performance Status: ${this.getOverallStatus()}
    `.trim();
  }
  
  /**
   * Get overall performance status
   */
  getOverallStatus(): 'excellent' | 'good' | 'warning' | 'critical' {
    const metrics = this.getMetrics();
    const recentAlerts = this.getAlerts();
    
    // Critical if any critical alerts
    if (recentAlerts.some(alert => alert.type === 'memory_limit')) {
      return 'critical';
    }
    
    // Warning if performance targets not met
    if (metrics.hitTestTime > this.targets.hitTestTime * 1.5 ||
        metrics.positioningTime > this.targets.positioningTime * 1.5 ||
        metrics.cacheHitRate < this.targets.cacheHitRate * 0.8) {
      return 'warning';
    }
    
    // Good if targets mostly met
    if (metrics.hitTestTime < this.targets.hitTestTime &&
        metrics.positioningTime < this.targets.positioningTime &&
        metrics.cacheHitRate > this.targets.cacheHitRate) {
      return 'excellent';
    }
    
    return 'good';
  }
  
  /**
   * Reset all metrics and history
   */
  reset(): void {
    this.operationHistory = [];
    this.alerts = [];
    this.cacheHits = 0;
    this.cacheMisses = 0;
    
    this.currentMetrics = {
      hitTestTime: 0,
      positioningTime: 0,
      renderingTime: 0,
      cacheHitRate: 0,
      totalOperations: 0,
      memoryUsage: {
        spatialIndex: 0,
        positionCache: 0,
        total: 0
      }
    };
    
    console.log('[CaretPerformanceMonitor] Metrics reset');
  }
  
  /**
   * Export performance data for analysis
   */
  exportData(): {
    metrics: PerformanceMetrics;
    targets: PerformanceTarget;
    operationHistory: OperationRecord[];
    alerts: PerformanceAlert[];
    timestamp: number;
  } {
    return {
      metrics: this.getMetrics(),
      targets: this.getTargets(),
      operationHistory: [...this.operationHistory],
      alerts: [...this.alerts],
      timestamp: Date.now()
    };
  }
}
